import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  serverTimestamp,
  setDoc,
  documentId,
  DocumentData,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Industry } from '../models/Industry';

// Collection name in Firestore
const INDUSTRIES_COLLECTION = 'industries';

export const convertToIndustry = (data: any): Industry => {
    return {
        id: data.id || '',
        name: data.name || '',
        description: data.description || '',
        commonIssues: data.commonIssues || [],
        keywords: data.keywords || [],
        statistics: {
            averageRevenue: data.statistics?.averageRevenue,
            marketSize: data.statistics?.marketSize,
            growthRate: data.statistics?.growthRate,
            employeeCount: data.statistics?.employeeCount,
            ...data.statistics
        },
        risks: data.risks || [],
        opportunities: data.opportunities || [],
        status: data.status || 'active',
        isActive: data.isActive ?? true,
        averageRevenue: data.averageRevenue || 0,
        complexity: data.complexity || 'medium',
        severity: data.severity || 'medium',
        internalNotes: data.internalNotes,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt) || new Date(),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt) || new Date()
    };
};

/**
 * Add a new industry to Firestore
 * @param industry Industry data to add
 * @returns The ID of the newly created industry
 */
export const addIndustry = async (industry: Omit<Industry, 'id'>): Promise<string | null> => {
  try {
    const industriesRef = collection(db, INDUSTRIES_COLLECTION);
    const docRef = await addDoc(industriesRef, {
      ...industry,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding industry:', error);
    return null;
  }
};

/**
 * Get all industries from Firestore
 * @returns Array of industries
 */
export const getIndustries = async (): Promise<Industry[]> => {
    try {
        const industriesRef = collection(db, INDUSTRIES_COLLECTION);
        const querySnapshot = await getDocs(industriesRef);
        return querySnapshot.docs.map(doc => {
            const data = doc.data();
            data.id = doc.id; // Add the document ID to the data
            return convertToIndustry(data);
        });
    } catch (error) {
        console.error('Error fetching industries:', error);
        return [];
    }
};

/**
 * Update an industry in Firestore
 * @param id Industry ID
 * @param updates Industry data to update
 * @returns Promise that resolves when the update is complete
 */
export const updateIndustry = async (id: string, updates: Partial<Industry>): Promise<void> => {
  try {
    const industryRef = doc(db, INDUSTRIES_COLLECTION, id);
    const updateData: Partial<Industry> = {
      ...updates,
      updatedAt: new Date()
    };

    // Remove id from updates if it exists
    if ('id' in updateData) {
      delete updateData.id;
    }

    await updateDoc(industryRef, updateData);
  } catch (error) {
    console.error('Error updating industry:', error);
  }
};

/**
 * Delete an industry from Firestore
 * @param id Industry ID
 * @returns Promise that resolves when the deletion is complete
 */
export const deleteIndustry = async (id: string): Promise<void> => {
  try {
    const industryRef = doc(db, INDUSTRIES_COLLECTION, id);
    await deleteDoc(industryRef);
  } catch (error) {
    console.error('Error deleting industry:', error);
  }
};

/**
 * Get industries by a specific field value
 * @param field Field name to query
 * @param value Field value to match
 * @returns Array of industries matching the query
 */
export const getIndustriesByField = async (field: keyof Industry, value: any): Promise<Industry[]> => {
  try {
    const industriesRef = collection(db, INDUSTRIES_COLLECTION);
    const q = query(industriesRef, where(field, '==', value));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name || '',
        status: data.status || 'pending',
        isActive: data.isActive ?? true,
        internalNotes: data.internalNotes || '',
        description: data.description || '',
        keywords: data.keywords || [],
        averageRevenue: data.averageRevenue || 0,
        complexity: data.complexity || 'low',
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as Industry;
    });
  } catch (error) {
    console.error(`Error getting industries by ${field}:`, error);
    throw new Error('Failed to load industries. Please try again.');
  }
};

export const getIndustryById = async (id: string): Promise<Industry | null> => {
    try {
        const industryRef = doc(db, INDUSTRIES_COLLECTION, id);
        const snapshot = await getDoc(industryRef);
        
        if (!snapshot.exists()) {
            return null;
        }

        return convertToIndustry(snapshot as QueryDocumentSnapshot<DocumentData>);
    } catch (error) {
        console.error('Error fetching industry:', error);
        return null;
    }
};

export const getIndustriesByIds = async (ids: string[]): Promise<Industry[]> => {
    try {
        const industries: Industry[] = [];
        // Firestore has a limit of 10 documents per in query
        for (let i = 0; i < ids.length; i += 10) {
            const batch = ids.slice(i, i + 10);
            const industriesRef = collection(db, INDUSTRIES_COLLECTION);
            const q = query(industriesRef, where(documentId(), 'in', batch));
            const querySnapshot = await getDocs(q);
            
            industries.push(...querySnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    name: data.name || '',
                    description: data.description || '',
                    commonIssues: data.commonIssues || [],
                    keywords: data.keywords || [],
                    statistics: {
                        averageRevenue: data.statistics?.averageRevenue || 0,
                        marketSize: data.statistics?.marketSize || 0,
                        growthRate: data.statistics?.growthRate || 0,
                        employeeCount: data.statistics?.employeeCount || 0
                    },
                    risks: data.risks || [],
                    opportunities: data.opportunities || [],
                    createdAt: data.createdAt?.toDate() || new Date(),
                    updatedAt: data.updatedAt?.toDate() || new Date()
                } as Industry;
            }));
        }
        return industries;
    } catch (error) {
        console.error('Error fetching industries by ids:', error);
        throw error;
    }
}; 